'use client';

import { Link } from 'react-router-dom';
import { subscribeNewsletter } from '@/lib/clientApi';
import { Server, Globe, Shield, Cpu, Database, Clock, Award, DollarSign, Check, ArrowRight, Star, Users, Send, Code } from "lucide-react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useState, useEffect } from "react";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Fixed variant structure - removed transition from the variant object
const fadeInScale = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 }
};

// Transition for fadeInScale
const fadeInScaleTransition = { duration: 0.5 };

// Interface for particle properties
interface Particle {
  width: number;
  height: number;
  top: string;
  left: string;
  opacity: number;
  duration: number;
}

// Generate random percentage between min and max
const getRandomPercentage = (min: number, max: number) => `${Math.floor(Math.random() * (max - min) + min)}%`;

// Generate random duration between min and max
const getRandomDuration = (min: number, max: number) => Math.random() * (max - min) + min;

const ResourceMonitor = () => {
  const cpuPercentage = useMotionValue(30);
  const memoryPercentage = useMotionValue(30);
  const storagePercentage = useMotionValue(30);

  const cpuUsage = useTransform(cpuPercentage, value => `${Math.round(value)}%`);
  const memoryUsage = useTransform(memoryPercentage, value => {
    const usedGB = Math.round((value * 256) / 100);
    return `${usedGB}GB/256GB`;
  });
  const storageUsage = useTransform(storagePercentage, value => {
    const usedTB = ((value * 10) / 100).toFixed(1);
    return `${usedTB}TB/10TB`;
  });

  const [loadValues, setLoadValues] = useState({
    cpu: 30,
    memory: 30,
    storage: 30
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadValues(prev => ({
        cpu: Math.min(80, Math.max(10, prev.cpu + (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 3))),
        memory: Math.min(80, Math.max(10, prev.memory + (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 3))),
        storage: Math.min(80, Math.max(10, prev.storage + (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 3)))
      }));
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mt-4 bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
      <h2 className="text-sm font-semibold text-white mb-3">Resource Monitor</h2>
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-slate-300 mb-1">
            <span>CPU Usage</span>
            <motion.span>{cpuUsage}</motion.span>
          </div>
          <div className="h-1 bg-slate-700 rounded">
            <motion.div
              className="h-full bg-green-400 rounded"
              animate={{ width: `${loadValues.cpu}%` }}
              onUpdate={latest => {
                if (typeof latest.width === 'string') {
                  cpuPercentage.set(parseFloat(latest.width));
                }
              }}
              transition={{ 
                duration: 3,
                ease: "easeInOut"
              }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-slate-300 mb-1">
            <span>Memory</span>
            <motion.span>{memoryUsage}</motion.span>
          </div>
          <div className="h-1 bg-slate-700 rounded">
            <motion.div
              className="h-full bg-blue-400 rounded"
              animate={{ width: `${loadValues.memory}%` }}
              onUpdate={latest => {
                if (typeof latest.width === 'string') {
                  memoryPercentage.set(parseFloat(latest.width));
                }
              }}
              transition={{ 
                duration: 3,
                ease: "easeInOut"
              }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-slate-300 mb-1">
            <span>Storage</span>
            <motion.span>{storageUsage}</motion.span>
          </div>
          <div className="h-1 bg-slate-700 rounded">
            <motion.div
              className="h-full bg-purple-400 rounded"
              animate={{ width: `${loadValues.storage}%` }}
              onUpdate={latest => {
                if (typeof latest.width === 'string') {
                  storagePercentage.set(parseFloat(latest.width));
                }
              }}
              transition={{ 
                duration: 3,
                ease: "easeInOut"
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  // State for particles
  const [particles, setParticles] = useState<Particle[]>([]);
  
  // Generate particles only on client-side
  useEffect(() => {
    const newParticles = Array.from({ length: 15 }).map(() => ({
      width: Math.random() * 6 + 2,
      height: Math.random() * 6 + 2,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      opacity: Math.random() * 0.3,
      duration: Math.random() * 10 + 10
    }));
    
    setParticles(newParticles);
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 md:pb-32 min-h-screen flex items-center -mt-8">
        {/* Background elements - Direct server/data center imagery */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 z-[-1]"></div>
        
        {/* Server rack and data center background */}
        <div className="absolute inset-0 z-0">
          {/* Server racks - left side */}
          <div className="absolute top-0 left-0 w-[40%] h-full opacity-15">
            {/* Vertical server racks */}
            {[...Array(3)].map((_, rackIndex) => (
              <div 
                key={`rack-${rackIndex}`}
                className="absolute h-[80%] w-[28%] border-2 border-primary-600/30 rounded-sm"
                style={{
                  top: '10%',
                  left: `${5 + rackIndex * 32}%`,
                }}
              >
                {/* Server units in rack */}
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={`server-${rackIndex}-${i}`}
                    className="absolute w-[90%] h-[6%] mx-[5%] bg-primary-800/20 border border-primary-600/30"
                    style={{
                      top: `${5 + i * 8}%`,
                    }}
                    animate={{
                      borderColor: [(i + rackIndex) % 4 === 0 ? 
                        'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.1)', 
                        (i + rackIndex) % 4 === 0 ? 
                        'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.1)', 
                        (i + rackIndex) % 4 === 0 ? 
                        'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.1)']
                    }}
                    transition={{
                      duration: 3 + ((i + rackIndex) % 5),
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    {/* Server lights */}
                    <div className="absolute top-1/2 right-[5%] transform -translate-y-1/2 flex space-x-1">
                      <motion.div 
                        className="w-1 h-1 rounded-full bg-green-400"
                        animate={{ 
                          opacity: (i + rackIndex) % 3 === 0 ? [0.3, 1, 0.3] : [0.3, 0.3, 0.3]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.div 
                        className="w-1 h-1 rounded-full bg-blue-400"
                        animate={{ 
                          opacity: (i + rackIndex) % 4 === 0 ? [0.3, 1, 0.3] : [0.3, 0.3, 0.3]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0.5,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            ))}
          </div>
          
          {/* Network connections - center */}
          <div className="absolute top-[15%] left-[40%] w-[20%] h-[70%] opacity-15">
            {/* Vertical backbone */}
            <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-primary-600/50 transform -translate-x-1/2"></div>
            
            {/* Horizontal connections */}
            {[...Array(8)].map((_, i) => (
              <div key={`connection-${i}`} className="absolute w-full h-[1px] bg-primary-600/30" style={{ top: `${10 + i * 12}%` }}>
                {/* Data packets */}
                <motion.div
                  className="absolute w-2 h-1 bg-primary-400/50 rounded-sm"
                  animate={{
                    left: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3),
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.5,
                  }}
                />
                
                <motion.div
                  className="absolute w-2 h-1 bg-secondary-400/50 rounded-sm"
                  animate={{
                    left: ['100%', '0%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 5 + (i % 2),
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.3 + 1,
                  }}
                />
              </div>
            ))}
            
            {/* Connection nodes */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={`node-${i}`}
                className="absolute w-3 h-3 rounded-full bg-primary-600/40 border border-primary-400/30"
                style={{
                  top: `${10 + i * 12}%`,
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  scale: i % 3 === 0 ? [1, 1.2, 1] : [1, 1, 1],
                  opacity: i % 3 === 0 ? [0.4, 0.7, 0.4] : [0.4, 0.4, 0.4],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Server monitoring screens - right side */}
          <div className="absolute top-[10%] right-[5%] w-[30%] h-[80%] opacity-15">
            {/* Main monitoring panel */}
            <div className="absolute inset-0 border-2 border-primary-600/30 rounded-md overflow-hidden">
              {/* Screen header */}
              <div className="absolute top-0 left-0 right-0 h-[8%] border-b border-primary-600/30">
                <div className="absolute top-1/2 left-[5%] transform -translate-y-1/2 flex space-x-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                </div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[40%] h-[3px] bg-primary-600/40"></div>
              </div>
              
              {/* CPU/Memory graphs */}
              <div className="absolute top-[12%] left-[5%] right-[5%] h-[25%]">
                {/* CPU graph */}
                <div className="absolute inset-0">
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-primary-600/20"></div>
                  <div className="absolute top-1/3 left-0 w-full h-[1px] bg-primary-600/20"></div>
                  <div className="absolute top-2/3 left-0 w-full h-[1px] bg-primary-600/20"></div>
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-primary-600/20"></div>
                  
                  {/* CPU usage line */}
                  <svg className="absolute inset-0" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <motion.path
                      d="M0,50 Q10,30 20,45 T40,35 T60,55 T80,25 T100,40"
                      stroke="rgba(59, 130, 246, 0.5)"
                      strokeWidth="1"
                      fill="none"
                      animate={{
                        d: [
                          "M0,50 Q10,30 20,45 T40,35 T60,55 T80,25 T100,40",
                          "M0,40 Q10,50 20,30 T40,45 T60,35 T80,55 T100,30",
                          "M0,50 Q10,30 20,45 T40,35 T60,55 T80,25 T100,40"
                        ]
                      }}
                      transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </svg>
                </div>
              </div>
              
              {/* Server status grid */}
              <div className="absolute top-[42%] left-[5%] right-[5%] bottom-[5%] grid grid-cols-3 grid-rows-4 gap-[2%]">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={`status-${i}`}
                    className="border border-primary-600/30 rounded-sm relative"
                    animate={{
                      borderColor: i % 5 === 0 ? 
                        ['rgba(59, 130, 246, 0.3)', 'rgba(59, 130, 246, 0.5)', 'rgba(59, 130, 246, 0.3)'] : 
                        ['rgba(59, 130, 246, 0.3)', 'rgba(59, 130, 246, 0.3)', 'rgba(59, 130, 246, 0.3)']
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.2,
                    }}
                  >
                    <motion.div 
                      className="absolute top-0 left-0 h-[2px] bg-green-400"
                      style={{ width: `${30 + (i % 7) * 10}%` }}
                      animate={{
                        width: i % 4 === 0 ? 
                          [`${30 + (i % 7) * 10}%`, `${50 + (i % 5) * 10}%`, `${30 + (i % 7) * 10}%`] : 
                          [`${30 + (i % 7) * 10}%`, `${30 + (i % 7) * 10}%`, `${30 + (i % 7) * 10}%`]
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.3,
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-24 md:pt-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                  Starting at just $1/month
                </span>
              </motion.div>
              
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="bg-clip-text text-transparent bg-gradient-rainbow">
                  1 Dollar Cloud
                </span>
              </motion.h1>
              
              <motion.h2
                className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight mb-4 text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.33 }}
              >
                <span className="text-white">
                  $1 to Power Your Online Presence.
                </span>
              </motion.h2>
              
              <motion.h3 
                className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-6 text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.35 }}
              >
                <span className="bg-clip-text text-transparent bg-gradient-rainbow">
                  Affordable Web Hosting
                </span>{" "}
                <span className="text-white">
                  for Everyone
                </span>
              </motion.h3>
              
              <motion.p 
                className="text-xl mb-4 text-slate-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Get reliable hosting solutions starting at just $1. Perfect for personal websites, small businesses, and growing enterprises.
              </motion.p>
              
              <motion.p 
                className="text-xl mb-8 text-slate-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.45 }}
              >
                Cheapest hosting without compromising on quality sets us apart from other hosting providers.
              </motion.p>
              
              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-5 mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto"
                >
                  <Link
                    to="/hosting"
                    className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon flex items-center justify-center w-full sm:w-auto"
                  >
                    <span className="mr-2">Get Started</span> <ArrowRight className="h-5 w-5" />
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="bg-slate-800/80 text-primary-400 hover:bg-slate-700 border border-slate-700/50 px-8 py-4 rounded-lg font-medium text-center transition-all shadow-md backdrop-blur-sm flex items-center justify-center w-full sm:w-auto"
                  >
                    <span>Contact Sales</span>
                  </Link>
                </motion.div>
              </motion.div>
              
              {/* Trust badges */}
              <motion.div 
                className="flex flex-wrap items-center gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <div className="flex items-center">
                  <div className="text-sm font-medium text-white">Trusted by 10,000+ customers</div>
                </div>
                <div className="flex items-center">
                  <div className="flex text-yellow-400">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                    {/* Half star for 4.5 rating */}
                    <div className="relative h-4 w-4">
                      <Star className="absolute h-4 w-4 text-yellow-400" style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }} />
                      <Star className="absolute h-4 w-4 text-yellow-400 fill-current" style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }} />
                      <Star className="absolute h-4 w-4 text-yellow-400/30" style={{ clipPath: 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)' }} />
                    </div>
                  </div>
                  <div className="ml-2">
                    <p className="text-sm font-medium text-white">4.5/5 rating</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Right Content - Server Monitoring */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden md:block relative md:col-span-5"
            >
              <div className="relative">
                {/* Glow effects */}
                <motion.div
                  className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-tertiary-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute -bottom-10 -right-10 w-60 h-60 bg-gradient-to-br from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />

                {/* Server Monitoring Display */}
                <motion.div
                  className="bg-slate-800/90 p-6 rounded-2xl shadow-3d border border-slate-700/50 backdrop-blur-sm"
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Server Status Display */}
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-900/90 p-3 rounded-lg border border-slate-700/50 z-20">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-xs text-slate-300">Server Online</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                      <span className="text-xs text-slate-300">Resources Available</span>
                    </div>
                  </div>

                  {/* Server Rack Units */}
                  <div className="space-y-3">
                    {[...Array(5)].map((_, index) => {
                      const [loadPercentage, setLoadPercentage] = useState(30);
                      const [isIncreasing, setIsIncreasing] = useState(true);

                      useEffect(() => {
                        const interval = setInterval(() => {
                          setLoadPercentage(current => {
                            // Randomly decide whether to increase or decrease
                            const shouldChangeDirection = Math.random() < 0.3; // 30% chance to change direction
                            if (shouldChangeDirection) {
                              setIsIncreasing(prev => !prev);
                            }

                            // Calculate new percentage
                            const change = Math.random() * 5; // Random change between 0-5%
                            const newValue = isIncreasing ? 
                              Math.min(80, current + change) : 
                              Math.max(10, current - change);

                            return newValue;
                          });
                        }, 2000); // Update every 2 seconds

                        return () => clearInterval(interval);
                      }, [isIncreasing]);

                      return (
                        <motion.div
                          key={index}
                          className="bg-slate-700/50 p-4 rounded-lg border border-slate-600/50 relative"
                          animate={{
                            borderColor: ['rgba(59, 130, 246, 0.2)', 'rgba(59, 130, 246, 0.4)', 'rgba(59, 130, 246, 0.2)']
                          }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Server className="h-5 w-5 text-primary-400 mr-2" />
                              <span className="text-sm text-slate-300">Server {index + 1}</span>
                            </div>
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse delay-100"></div>
                            </div>
                          </div>
                          <div className="h-1 bg-slate-600 rounded">
                            <motion.div
                              className="h-full bg-primary-400 rounded"
                              animate={{
                                width: `${loadPercentage}%`
                              }}
                              transition={{
                                duration: 2,
                                ease: "easeInOut"
                              }}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Resource Monitor */}
                  <ResourceMonitor />

                  {/* Status Badge */}
                    <div className="absolute -bottom-13 left-1/2 transform -translate-x-1/2 bg-gradient-rainbow text-white p-4 rounded-xl shadow-neon">
                      <div className="flex items-center">
                      <Server className="h-6 w-6 mr-2" />
                        <span className="font-bold whitespace-nowrap">Optimized Performance</span>
                      </div>
                    </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hosting Plans Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Pricing Plans
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Hosting Plans</span> for Every Need
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Choose the perfect hosting solution for your website or application.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                name: "Shared Hosting",
                description: "Perfect for personal websites, blogs, and small businesses getting started online.",
                price: "Starting from $1.00",
                link: "/hosting/shared-hosting",
                color: "from-primary-500 to-primary-700",
                popular: true
              },
              {
                name: "WordPress Hosting",
                description: "Optimized for WordPress with enhanced performance and auto-updates.",
                price: "Starting from $1.50",
                link: "/hosting/wordpress-hosting",
                color: "from-secondary-500 to-secondary-700"
              },
              {
                name: "VPS Hosting",
                description: "Dedicated resources with full control and scalability for growing businesses.",
                price: "Starting from $5.00",
                link: "/hosting/vps-hosting",
                color: "from-tertiary-500 to-tertiary-700"
              },
              {
                name: "Dedicated Server",
                description: "Enterprise-grade servers with maximum performance for high-traffic websites.",
                price: "Starting from $49.00",
                link: "/hosting/dedicated-server",
                color: "from-accent-500 to-accent-700"
              }
            ].map((plan, index) => (
              <motion.div 
                key={plan.name}
                className={`bg-slate-800 rounded-xl shadow-xl overflow-hidden border ${plan.popular ? 'border-primary-500' : 'border-slate-700'} flex flex-col relative group`}
                variants={fadeInScale}
                transition={fadeInScaleTransition}
                whileHover={{ y: -15, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-rainbow text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                    POPULAR
                  </div>
                )}
                <div className={`p-6 bg-gradient-to-r ${plan.color} border-b border-slate-700`}>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline">
                    <span className="text-base font-bold text-white">{plan.price}</span>
                  </div>
                  <p className="text-sm text-white/80 mt-2">{plan.description}</p>
                </div>
                <div className="p-6 flex-grow">
                  <ul className="space-y-3">
                    {(plan.name === "Shared Hosting" ? [
                      "Lightning-Fast Page Loading",
                      "Easy Website Builder",
                      "One-Click App Installer",
                      "Free Domain Transfer",
                      "99.9% Uptime Guarantee",
                      "24/7 Expert Support"
                    ] : plan.name === "WordPress Hosting" ? [
                      "WordPress Pre-installed",
                      "Auto WordPress Updates",
                      "Enhanced Security",
                      "WP Optimized Servers",
                      "Free Theme Installation",
                      "Expert WP Support"
                    ] : plan.name === "VPS Hosting" ? [
                      "Dedicated Resources",
                      "Full Root Access",
                      "Instant Scalability",
                      "SSD Storage",
                      "Cloud Infrastructure",
                      "Advanced Security"
                    ] : [
                      "Enterprise Hardware",
                      "Dedicated IP Address",
                      "Custom Configuration",
                      "Premium Support",
                      "DDoS Protection",
                      "100% Resource Control"
                    ]).map((feature, index) => (
                      <li key={`${plan.name}-feature-${index}`} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
          </li>
                    ))}
                  </ul>
                </div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-6 pt-0"
                >
                  <Link
                    to={plan.link}
                    className={`block w-full ${plan.popular ? 'bg-gradient-rainbow' : 'bg-gradient-to-r ' + plan.color} text-white text-center py-3 px-4 rounded-lg transition-all shadow-lg`}
                  >
                    Get Started
                  </Link>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Domain Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Domain Registration
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Secure Your <span className="text-transparent bg-clip-text bg-gradient-rainbow">Perfect Domain</span> Name
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Establish your online identity with a domain name that represents your brand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="bg-slate-800/80 p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Globe className="mr-3 h-6 w-6 text-primary-400" />
                  Why Choose Our Domain Services
                </h3>
                <p className="text-slate-300 mb-4">
                  At 1 Dollar Cloud, we make domain registration simple, affordable, and hassle-free. Our domain services are designed to complement our hosting solutions, providing you with a one-stop solution for your online presence.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Competitive pricing with no hidden fees</span>
          </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Free WHOIS privacy protection included</span>
          </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Easy domain management through our control panel</span>
          </li>
                </ul>
              </div>

              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Link
                  to="/domains"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon flex items-center justify-center w-fit"
                >
                  <span className="mr-2">Explore Domain Options</span> <ArrowRight className="h-5 w-5" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              {[
                {
                  icon: <DollarSign className="h-6 w-6" />,
                  title: "Affordable Pricing",
                  description: "Get the best domain names at competitive prices, with transparent renewal rates and no surprise fees."
                },
                {
                  icon: <Shield className="h-6 w-6" />,
                  title: "Privacy Protection",
                  description: "Keep your personal information private with free WHOIS privacy protection on all domain registrations."
                },
                {
                  icon: <Database className="h-6 w-6" />,
                  title: "DNS Management",
                  description: "Full control over your domain's DNS settings with an easy-to-use management interface."
                },
                {
                  icon: <Award className="h-6 w-6" />,
                  title: "Domain Transfers",
                  description: "Seamlessly transfer your existing domains to us and manage everything in one place."
                }
              ].map((feature, index) => (
                <motion.div 
                  key={feature.title}
                  className="bg-slate-800 p-6 rounded-xl shadow-xl border border-slate-700 group"
                  variants={fadeInScale}
                  transition={fadeInScaleTransition}
                  whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                >
                  <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 group-hover:bg-gradient-rainbow transition-all duration-500">
                    <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                      {feature.icon}
        </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-300 text-sm">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Why Choose Us
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud</span>?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              We provide reliable, high-performance hosting solutions at unbeatable prices.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                icon: <Cpu className="h-6 w-6" />,
                title: "Maximum Performance",
                description: "Experience maximum performance with 1 Dollar Cloud—fast, reliable hosting at an unbeatable price."
              },
              {
                icon: <Shield className="h-6 w-6" />,
                title: "Safe & Secured",
                description: "With 1 Dollar Cloud, your data is protected by advanced security for a safe and secure hosting experience."
              },
              {
                icon: <Server className="h-6 w-6" />,
                title: "99% Server Uptime",
                description: "Enjoy 99.99% uptime with 1 Dollar Cloud—reliable, high-performance hosting you can count on."
              },
              {
                icon: <Check className="h-6 w-6" />,
                title: "Hassle-Free Setup",
                description: "Experience a hassle-free setup with 1 Dollar Cloud—easy, quick, and straightforward hosting from day one."
              },
              {
                icon: <Globe className="h-6 w-6" />,
                title: "Unmetered Bandwidth",
                description: "Enjoy the freedom of unmetered bandwidth with 1 Dollar Cloud—power your site without restrictions."
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: "Dedicated Support",
                description: "Get dedicated support with 1 Dollar Cloud—our team is here to assist you 24/7 via Chat, Email and Support Tickets."
              }
            ].map((feature, index) => (
              <motion.div 
                key={feature.title}
                className="bg-slate-800 p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-700 group"
                variants={fadeInScale}
                transition={fadeInScaleTransition}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Worldwide Data Servers Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Global Infrastructure
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Worldwide</span> Data Servers
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience lightning-fast loading times with our strategically positioned data centers across the globe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="bg-slate-800/80 p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Globe className="mr-3 h-6 w-6 text-primary-400" />
                  Global Network
                </h3>
                <p className="text-slate-300 mb-4">
                  Our 12 strategically located data centers ensure your website loads quickly for visitors anywhere in the world. With an average global latency of just 45ms, your users experience lightning-fast performance regardless of their location.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Redundant network connections with 99.99% uptime guarantee</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Automatic content distribution to the nearest data center</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Real-time traffic routing optimization for minimal latency</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">North America</h4>
                    <span className="text-green-400 text-sm font-medium">~18ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers covering US East, US West, and Canada</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Europe</h4>
                    <span className="text-green-400 text-sm font-medium">~22ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">4 data centers in UK, Germany, France, and Netherlands</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Asia Pacific</h4>
                    <span className="text-green-400 text-sm font-medium">~35ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers in Singapore, Japan, and Australia</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Other Regions</h4>
                    <span className="text-green-400 text-sm font-medium">~40ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">2 data centers in Brazil and South Africa</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#141e30] p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm relative overflow-hidden">
                {/* World Map */}
                <div className="relative h-[400px] w-full">
                  <div className="absolute inset-0">
          <img
                      src="/images/world-map.png"
                      alt="World Map"
                      width={1000}
                      height={500}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Server Locations */}
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(17% - 9px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30"></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US West
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(28% - 10px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US East
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(38% - 12px)', left: 'calc(54% - 18px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        UK
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(36% - 12px)', left: 'calc(20% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.9s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Canada
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(40% - 12px)', left: 'calc(60% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Germany
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(30% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.6s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Russia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(60% - 12px)', left: 'calc(69% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        India
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.8s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Singapore
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(86% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.1s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Japan
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(74% - 12px)', left: 'calc(84% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.5s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Australia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(33% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Brazil
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(77% - 12px)', left: 'calc(55% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        South Africa
                      </div>
                    </div>
                  </div>
                  
                  {/* Latency indicators */}
                  <div className="absolute bottom-4 right-4 bg-slate-900/80 p-3 rounded-lg border border-slate-700/50 backdrop-blur-sm">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <span className="text-xs text-slate-300">Low Latency (&lt;30ms)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <span className="text-xs text-slate-300">Medium Latency (30-60ms)</span>
                    </div>
                  </div>
                </div>
                
                {/* Overlay glow effects */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/10 to-tertiary-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-secondary-500/10 to-primary-500/10 rounded-full blur-3xl"></div>
              </div>
              
              <div className="absolute -bottom-5 -right-5 bg-gradient-rainbow text-white p-4 rounded-xl shadow-neon">
                <div className="flex items-center">
                  <Clock className="h-6 w-6 mr-2" />
                  <span className="font-bold">45ms Avg. Global Latency</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Testimonials
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">Customers</span> Say
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our customers have to say.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                quote: "1 Dollar Cloud has been a game-changer for my small business. Affordable, reliable, and their support team is always there when I need them.",
                author: "Sarah Johnson",
                role: "Small Business Owner",
                image: "/assets/testimonials/testimonial-1.jpg",
                color: "from-primary-500 to-tertiary-500"
              },
              {
                quote: "I've tried many hosting providers, but none offer the value that 1 Dollar Cloud does. Their VPS hosting is powerful and surprisingly affordable.",
                author: "Michael Chen",
                role: "Web Developer",
                image: "/assets/testimonials/testimonial-2.jpg",
                color: "from-secondary-500 to-primary-500"
              },
              {
                quote: "Setting up my WordPress site was a breeze with 1 Dollar Cloud. The performance is excellent and I haven't experienced any downtime.",
                author: "Emily Rodriguez",
                role: "Blogger",
                image: "/assets/testimonials/testimonial-3.jpg",
                color: "from-accent-500 to-tertiary-500"
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={testimonial.author}
                className="bg-slate-800 p-8 rounded-xl shadow-xl border border-slate-700 relative"
                variants={fadeInScale}
                transition={fadeInScaleTransition}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <div className="absolute top-0 right-0 transform -translate-y-1/2 translate-x-1/2">
                  <div className={`bg-gradient-to-r ${testimonial.color} text-white p-3 rounded-full shadow-lg`}>
                    <Users className="h-6 w-6" />
                  </div>
                </div>
                <div className="pt-4">
                  <div className="flex text-yellow-400 mb-4">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                    {/* Half star for 4.5 rating */}
                    <div className="relative h-4 w-4">
                      <Star className="absolute h-4 w-4 text-yellow-400" style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }} />
                      <Star className="absolute h-4 w-4 text-yellow-400 fill-current" style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }} />
                      <Star className="absolute h-4 w-4 text-yellow-400/30" style={{ clipPath: 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)' }} />
                    </div>
                  </div>
                  <p className="text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className={`bg-gradient-to-r ${testimonial.color} text-white font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3`}>
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <h2 className="font-semibold text-white">{testimonial.author}</h2>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Frequently Asked Questions
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Got <span className="text-transparent bg-clip-text bg-gradient-rainbow">Questions</span>?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Find answers to commonly asked questions about 1 Dollar Cloud hosting services.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                question: "What is 1 Dollar Cloud?",
                answer: "Dollar Cloud provides affordable and reliable cloud hosting solutions starting at just $1."
              },
              {
                question: "What types of hosting do you offer?",
                answer: "We offer Shared Hosting, VPS Hosting, Dedicated Hosting, and WordPress Hosting tailored to meet diverse needs."
              },
              {
                question: "Is there really no catch with the $1 hosting?",
                answer: "No catch! Our $1 hosting plan offers genuine value with all the essential features you need to get started. We're able to offer this price by optimizing our infrastructure and operations. While the plan has some limitations compared to our higher-tier plans, it provides excellent performance and reliability for small websites and beginners."
              },
              {
                question: "How reliable is your $1 hosting service?",
                answer: "Our $1 hosting service is built on the same reliable infrastructure as our premium plans. We guarantee 99.99% uptime, fast loading speeds, and secure hosting environment. Your website will be hosted on modern servers with SSD storage for optimal performance."
              },
              {
                question: "Can I upgrade my plan later if I need more resources?",
                answer: "Absolutely! You can easily upgrade to a higher plan at any time through your control panel. When you upgrade, we'll automatically migrate your website and data to the new plan with no downtime. You'll only pay the difference between your current plan and the new one."
              },
              {
                question: "How do I migrate over to your 1 Dollar Cloud hosting service?",
                answer: "To migrate to 1 Dollar Cloud, simply sign up for a hosting plan, and our support team will guide you through the process of transferring your website or server. We offer seamless migration assistance to ensure a smooth transition."
              },
              {
                question: "Why do I need a website?",
                answer: "A website is essential for establishing your online presence, building credibility, reaching a broader audience, and growing your business. It serves as a platform for showcasing your products or services, engaging with customers, and providing important information, helping you stand out in a competitive digital world."
              },
              {
                question: "How do I get started with 1 Dollar Cloud?",
                answer: "Simply sign up on our website, choose a hosting plan, and start setting up your server or website."
              },
              {
                question: "Is my data secure with 1 Dollar Cloud?",
                answer: "Yes, we use advanced security measures, including firewalls, encryption, and 24/7 monitoring, to keep your data safe and secure."
              },
              {
                question: "Do you offer unmetered bandwidth?",
                answer: "Yes, all of our plans include unmetered bandwidth to ensure seamless performance without limits."
              },
              {
                question: "Can I scale my hosting plan later?",
                answer: "Absolutely! Our plans are designed to grow with your needs. Upgrade anytime without disruptions."
              },
              {
                question: "What is the uptime guarantee?",
                answer: "We guarantee 99.99% uptime to ensure your website or application remains accessible at all times."
              },
              {
                question: "Do you provide customer support?",
                answer: "Yes, we offer 24/7 dedicated support to assist you with any issues or questions."
              },
              {
                question: "Is there a refund policy?",
                answer: "We offer refunds under certain conditions as outlined in our Refund Policy Page. Contact our support team for more details."
              },
              {
                question: "Can I host multiple websites?",
                answer: "Yes, depending on the plan you choose, you can host multiple websites on a single account."
              }
            ].map((faq, index) => (
              <motion.div 
                key={`faq-${index}`}
                className="mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <details className="group bg-slate-800/80 rounded-lg border border-slate-700/50 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <h3 className="text-lg font-medium text-white">{faq.question}</h3>
                    <div className="text-primary-400 group-open:rotate-180 transition-transform duration-300">
                      <ArrowRight className="h-5 w-5 transform rotate-90" />
                    </div>
                  </summary>
                  <div className="px-5 pb-5 text-slate-300">
                    <p>{faq.answer}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background elements - Data center visualization */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Server visualization background */}
        <div className="absolute inset-0">
          {/* Connection lines */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`line-${i}`}
                className="absolute bg-primary-600/10 h-[1px]"
                style={{
                  top: `${5 + i * 5}%`,
                  left: '0',
                  right: '0',
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                }}
                transition={{
                  duration: 3 + (i % 5),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.1,
                }}
              />
            ))}
            
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`vline-${i}`}
                className="absolute bg-primary-600/10 w-[1px]"
                style={{
                  left: `${5 + i * 5}%`,
                  top: '0',
                  bottom: '0',
                }}
                animate={{
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4 + (i % 3),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Data nodes */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => {
              const size = 2 + (i % 3) * 2;
              return (
                <motion.div
                  key={`node-${i}`}
                  className="absolute rounded-full bg-primary-500/30"
                  style={{
                    width: size,
                    height: size,
                    top: `${10 + (i * 73) % 80}%`,
                    left: `${5 + (i * 57) % 90}%`,
                  }}
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + (i % 4),
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              );
            })}
          </div>
          
          {/* Data flow animations */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => {
              const isVertical = i % 2 === 0;
              const size = isVertical ? '1px' : '2px';
              const startPosition = `${10 + (i * 37) % 80}%`;
              
              return (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute bg-gradient-to-r from-primary-500/0 via-primary-500/50 to-primary-500/0"
                  style={{
                    width: isVertical ? size : '100px',
                    height: isVertical ? '50px' : size,
                    top: isVertical ? '0' : startPosition,
                    left: isVertical ? startPosition : '0',
                  }}
                  animate={{
                    [isVertical ? 'top' : 'left']: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3) * 2,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.2,
                  }}
                />
              );
            })}
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Get Started Today
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Ready to <span className="text-transparent bg-clip-text bg-gradient-rainbow">Get Started</span>?
              </h2>
              
              <p className="text-xl mb-8 text-slate-300">
                Choose the perfect hosting plan for your WordPress site and get online today.
              </p>
              
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/hosting"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                className="mt-12 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-white mb-4">Subscribe to our newsletter</h3>
                <form onSubmit={async (e) => {
                   e.preventDefault();
                   const form = e.target as HTMLFormElement;
                   const email = (form.elements.namedItem('email') as HTMLInputElement).value;
                   let message = '';
                   let color = '';
                   if (email) {
                     const result = await subscribeNewsletter(email);
                     if (result.ok) {
                       message = result.message;
                        color = 'green';
                        (form.elements.namedItem('email') as HTMLInputElement).value = ''; // Clear the input field
                      } else {
                        message = result.message;
                        color = 'red';
                      }
                    } else {
                      message = 'Please enter your email address.';
                      color = 'red';
                    }
                    const subscribeMessage = document.getElementById('subscribe-message');
                    if (subscribeMessage) {
                      subscribeMessage.textContent = message;
                      subscribeMessage.style.color = color;
                    }
                  }}>
                   <div className="flex flex-col sm:flex-row">
                     <input
                       type="email"
                      name="email"
                      placeholder="Your email"
                      className="bg-slate-800/80 text-white rounded-l-lg sm:rounded-r-none rounded-r-lg sm:rounded-b-none rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full border border-slate-700 mb-2 sm:mb-0"
                    />
                    <motion.button
                      type="submit"
                      className="bg-gradient-rainbow text-white px-6 py-3 rounded-l-lg sm:rounded-l-none rounded-r-lg font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Send className="mr-2" />
                      <span>Subscribe</span>
                    </motion.button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    By subscribing, you agree to our Privacy Policy and consent to receive updates.
                  </p>
                  <p id="subscribe-message" className="mt-2 text-base text-center"></p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
